/**
 * 主要会場プリセット（design §15・re:Invent 2026 公式Venue準拠）。
 *
 * 座標は概算（施設中心）。会場ピンは常設表示で、参加者ピンとは別レイヤー・別アイコン。
 * ⚠️ 会場リストは年により変動。実装直前に公式セッションカタログで最終確認する。
 *
 * catalogVenueId は公式セッションカタログの Venue フィルタの内部 ID（タスク45）。
 * カタログで Venue を選ぶと URL に付く `search.venue=<ID>` の値（2026-10-03 にブラウザで取得）。
 * カタログのデータ自体は持たない（re:Invent は参加登録制でカタログは非公開・公開 HP に載せない）。
 * ⚠️ 年度・カタログ改修で ID は変わりうる。リンク先が絞り込まれなくなったら取り直す。
 */

/** 公式セッションカタログ（re:Invent 2026）。閲覧には参加登録とログインが必要。 */
const CATALOG_BASE_URL =
  'https://registration.awsevents.com/flow/awsevents/reinvent2026/event-catalog/page/eventCatalog';

export interface Venue {
  id: string;
  /** 日本語表示名 */
  name: string;
  /** 英語表示名 */
  nameEn: string;
  /** [lng, lat] */
  coordinates: [number, number];
  /** アイコン絵文字（会場種別の目印） */
  emoji: string;
  /** 公式セッションカタログの Venue フィルタ ID（セッションが無い会場は未設定） */
  catalogVenueId?: string;
}

export const VENUES: Venue[] = [
  {
    id: 'caesars-forum',
    name: 'シーザーズフォーラム',
    nameEn: 'Caesars Forum',
    coordinates: [-115.1668, 36.1188],
    emoji: '🏛️',
    catalogVenueId: '1714149514282001ui9z',
  },
  {
    id: 'caesars-palace',
    name: 'シーザーズパレス',
    nameEn: 'Caesars Palace',
    coordinates: [-115.1745, 36.1162],
    emoji: '🏰',
    catalogVenueId: '1790019845347001msk7',
  },
  {
    id: 'mgm-grand',
    name: 'MGM グランド',
    nameEn: 'MGM Grand',
    coordinates: [-115.1697, 36.1023],
    emoji: '🎰',
    catalogVenueId: '1714149514282004uw2u',
  },
  {
    id: 'venetian',
    name: 'ベネチアン',
    nameEn: 'The Venetian',
    coordinates: [-115.1698, 36.1213],
    emoji: '🎭',
    catalogVenueId: '1714149514282006uDYu',
  },
  {
    id: 'wynn-encore',
    name: 'ウィン / アンコール',
    nameEn: 'Wynn / Encore',
    coordinates: [-115.1655, 36.1264],
    emoji: '🦢',
    catalogVenueId: '1714149514282007uKju',
  },
  {
    id: 'las-airport',
    name: 'ハリー・リード空港 (LAS)',
    nameEn: 'Harry Reid Airport (LAS)',
    coordinates: [-115.1537, 36.084],
    emoji: '✈️',
  },
  {
    id: 'strip-other',
    name: 'ストリップ（その他）',
    nameEn: 'The Strip (other)',
    coordinates: [-115.1728, 36.1147],
    emoji: '🌃',
  },
];

/** 会場で絞り込んだ公式セッションカタログの URL。カタログ ID が無い会場は null。 */
export function venueCatalogUrl(venue: Venue): string | null {
  if (!venue.catalogVenueId) {
    return null;
  }
  const params = new URLSearchParams({ 'search.venue': venue.catalogVenueId });
  return `${CATALOG_BASE_URL}?${params.toString()}`;
}

/** 会場を GeoJSON FeatureCollection に変換する。 */
export function venuesToGeoJSON(): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: 'FeatureCollection',
    features: VENUES.map((v) => ({
      type: 'Feature',
      properties: {
        id: v.id,
        name: v.name,
        nameEn: v.nameEn,
        emoji: v.emoji,
        // 絵文字を描いたアイコン画像の ID（venueLayer で addImage 登録・icon-image で参照）。
        iconId: `venue-${v.id}`,
      },
      geometry: { type: 'Point', coordinates: v.coordinates },
    })),
  };
}

/** 2点間の距離（メートル・ハバーサイン）。 */
export function distanceMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * 指定座標の最寄り会場を返す（しきい値内のみ）。無ければ null。
 * 投稿フォームで「◯◯付近」の表示に使う（逆ジオコーディング不要）。
 * @param thresholdMeters この距離以内の会場だけ対象（既定 800m）
 */
export function nearestVenue(
  lat: number,
  lng: number,
  thresholdMeters = 800,
): Venue | null {
  let best: Venue | null = null;
  let bestDist = Infinity;
  for (const v of VENUES) {
    const d = distanceMeters(lat, lng, v.coordinates[1], v.coordinates[0]);
    if (d < bestDist) {
      bestDist = d;
      best = v;
    }
  }
  return best && bestDist <= thresholdMeters ? best : null;
}
